import React, { useState, useEffect, useRef } from 'react';
import apiService from '../services/apiService';
import './AgentDashboard.css';

const STORAGE_KEY = 'aliado_agent_chat';

const AgentDashboard = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        loadUser();
    }, []);

    // Load companies once user is available
    useEffect(() => {
        if (!user?.user_id) return;
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Restore messages from localStorage once user is loaded
    useEffect(() => {
        if (!user?.user_id) return;
        try {
            const stored = localStorage.getItem(`${STORAGE_KEY}_${user.user_id}`);
            if (stored) {
                setMessages(JSON.parse(stored));
            }
        } catch (e) {
            console.error('Erro ao restaurar histórico do chat:', e);
        }
    }, [user]);

    // Persist messages to localStorage on change
    useEffect(() => {
        if (!user?.user_id) return;
        try {
            localStorage.setItem(`${STORAGE_KEY}_${user.user_id}`, JSON.stringify(messages));
        } catch (e) {
            console.error('Erro ao salvar histórico do chat:', e);
        }
    }, [messages, user]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        if (!loading) {
            textareaRef.current?.focus();
        }
    }, [messages, loading]);

    const loadUser = async () => {
        try {
            const userData = await apiService.getUserInfo();
            setUser(userData.user);
        } catch (error) {
            console.error('Erro ao carregar usuário:', error);
        }
    };

    const loadCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const data = await apiService.getCompaniesByUser(user.user_id);
            setCompanies(data.companies || []);
        } catch (error) {
            console.error('Erro ao carregar empresas:', error);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const handleSend = async () => {
        const question = input.trim();
        if (!question || loading) return;

        const subdomain = selectedCompany?.subdomain;
        const userId = user?.user_id;

        if (!userId || !subdomain) return;

        const userMessage = {
            role: 'user',
            content: question,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        try {
            const chat_history = messages
                .filter(m => m.role === 'user' || m.role === 'assistant')
                .map(({ role, content }) => ({ role, content }));

            const response = await apiService.agentQuery({
                subdomain,
                question,
                user_id: userId,
                chat_history
            });

            const assistantMessage = {
                role: 'assistant',
                content: response.answer || 'Sem resposta do agente.',
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            const errorMessage = {
                role: 'error',
                content: error.message || 'Erro ao consultar o agente.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        // Auto-resize
        e.target.style.height = 'auto';
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
    };

    const handleClear = () => {
        setMessages([]);
        if (user?.user_id) {
            localStorage.removeItem(`${STORAGE_KEY}_${user.user_id}`);
        }
    };

    const formatTime = (date) => {
        return new Date(date).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="agent-dashboard">
            <div className="agent-dashboard-header">
                <h2>🤖 Agente Dashboard</h2>
                {messages.length > 0 && (
                    <button className="agent-clear-btn" onClick={handleClear}>
                        🗑️ Limpar Chat
                    </button>
                )}
            </div>

            <div className="agent-messages">
                {messages.length === 0 && !loading && (
                    <div className="agent-empty">
                        <span className="agent-empty-icon">🤖</span>
                        <p>Envie uma pergunta para o agente de IA</p>
                    </div>
                )}

                {messages.map((msg, index) => {
                    if (msg.role === 'error') {
                        return (
                            <div key={index} className="agent-error">
                                ⚠️ {msg.content}
                            </div>
                        );
                    }
                    return (
                        <div key={index} className={`agent-message ${msg.role}`}>
                            <div className="agent-message-bubble">
                                {msg.content}
                            </div>
                            <span className="agent-message-time">
                                {formatTime(msg.timestamp)}
                            </span>
                        </div>
                    );
                })}

                {loading && (
                    <div className="agent-typing">
                        <div className="agent-typing-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        Pensando...
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="agent-input-area">
                <div className="agent-company-selector">
                    <label className="agent-company-label">Selecione a empresa:</label>
                    <div className="agent-company-pills">
                        {loadingCompanies ? (
                            <span className="agent-company-loading">Carregando empresas...</span>
                        ) : companies.map(company => (
                            <button
                                key={company.id}
                                className={`agent-company-pill ${selectedCompany?.id === company.id ? 'active' : ''}`}
                                onClick={() => setSelectedCompany(company)}
                            >
                                {company.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="agent-input-row">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleTextareaChange}
                        onKeyDown={handleKeyDown}
                        placeholder={loading ? 'Aguardando resposta...' : 'Digite sua pergunta...'}
                        rows={1}
                        readOnly={loading}
                    />
                    <button
                        className="agent-send-btn"
                        onClick={handleSend}
                        disabled={!input.trim() || loading || !selectedCompany}
                    >
                        Enviar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgentDashboard;
